import { statusColumnHeaderMessage } from '@app/modules/audit/components/create-status-column';
import { SchedulesListCategory } from '@app/modules/audit/modules/station-audit/components/audit-result/build-schedules-list-order-filter-url';
import { ConfirmedByScheduleOrderFilterButton } from '@app/modules/audit/modules/station-audit/components/audit-result/ConfirmedByScheduleOrderFilterButton';
import { orderFieldMessage } from '@app/modules/audit/modules/station-audit/components/OrderField';
import { versionFieldMessage } from '@app/modules/audit/modules/station-audit/components/VersionField';
import { AuditSchedule } from '@mi-front/core';
import { Box, Stack, Typography } from '@mui/material';
import { MessageDescriptor, useIntl } from 'react-intl';
import {
  getVerifyMatchLinkedSchedule,
  getVerifyMatchSchedulesListCategory,
} from './verify-match-target-utils';
import { VerifyMatchTarget } from './verify-match-types';
import { VerifyMatchStatus } from './VerifyMatchStatus';

export interface VerifyMatchScheduleMetaProps {
  target: VerifyMatchTarget;
}

type ScheduleMetaField = {
  label: MessageDescriptor;
  value: string;
  /** When set, render a schedules-list filter button next to the value. */
  filterCategory?: SchedulesListCategory;
};

/**
 * Resolves order/version and schedules-list category from the schedule linked to the detection.
 */
function getScheduleMeta(target: VerifyMatchTarget): {
  schedule: AuditSchedule | null | undefined;
  fields: ScheduleMetaField[];
} {
  const schedule = getVerifyMatchLinkedSchedule(target);
  const category = getVerifyMatchSchedulesListCategory(target);
  const order = schedule?.order?.trim() ?? '';
  const version = schedule?.version?.trim() ?? '';

  const fields: ScheduleMetaField[] = [
    {
      label: orderFieldMessage,
      value: order,
      filterCategory: order !== '' ? category : undefined,
    },
    { label: versionFieldMessage, value: version },
  ].filter((field) => field.value !== '');

  return { schedule, fields };
}

/**
 * Shows order, version, and status from the match at the top of the verify dialog content.
 */
export function VerifyMatchScheduleMeta({ target }: VerifyMatchScheduleMetaProps) {
  const { $t } = useIntl();
  const { schedule, fields } = getScheduleMeta(target);

  return (
    <Stack direction="row" flexWrap="wrap" useFlexGap spacing={4} alignItems="flex-start">
      {fields.map((field) => (
        <Box key={field.label.id}>
          <Typography variant="caption" color="text.secondary" display="block">
            {$t(field.label)}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="body2">{field.value}</Typography>
            {field.filterCategory != null && schedule != null && (
              <ConfirmedByScheduleOrderFilterButton
                order={field.value}
                scheduleDatetime={schedule.datetime}
                category={field.filterCategory}
              />
            )}
          </Box>
        </Box>
      ))}
      <Box>
        <Typography variant="caption" color="text.secondary" display="block">
          {$t(statusColumnHeaderMessage)}
        </Typography>
        <VerifyMatchStatus target={target} />
      </Box>
    </Stack>
  );
}
